/**
 * Single Source of Truth: Architectural Skills & Competency Matrix
 */

export const skillDomains = [
    {
        id: 'backend',
        title: 'Backend Services & Microservices',
        badge: 'Priority #1: High-Concurrency & APIs',
        description: 'Designing decoupled, fault-tolerant backend architectures engineered for high-throughput event loops and sub-50ms message propagation.',
        technologies: [
            'Node.js',
            'Express.js',
            'Kafka',
            'Socket.IO',
            'WebSockets',
            'Python',
            'RESTful APIs',
            'Razorpay Payment Gateway',
            'Event-Driven Microservices',
        ],
        competencies: [
            {
                label: 'Cluster Orchestration',
                detail: 'Multi-core Node.js worker scaling & asynchronous non-blocking event loops',
            },
            {
                label: 'Real-Time Streaming',
                detail: 'Event-driven publish/subscribe pipelines via Apache Kafka & Socket.IO WebSockets',
            },
            {
                label: 'Cryptographic Security',
                detail: 'HMAC-SHA256 webhook validation, JWT rotation & defense-in-depth auth',
            },
            {
                label: 'System Resilience',
                detail: 'Distributed rate limiting, circuit breaker patterns & graceful failover',
            },
            {
                label: 'API Platform Engineering',
                detail: 'High-throughput RESTful services, database connection pooling & sub-50ms query execution',
            },
        ],
    },
    {
        id: 'frontend',
        title: 'Frontend Systems & UI Engineering',
        badge: 'Priority #2: Web & Mobile Platforms',
        description: 'Engineering resilient web and cross-platform mobile applications with modern rendering paradigms, deterministic state, and sub-second performance budgets.',
        technologies: [
            'Next.js (App Router / SSR)',
            'React',
            'React Native (iOS & Android)',
            'TypeScript',
            'Tailwind CSS',
            'HTML5 / Modern CSS',
            'Webpack / Vite',
            'State Management (Redux/Zustand)',
        ],
        competencies: [
            {
                label: 'Rendering Architecture',
                detail: 'React Server Components (RSC) & streaming server-side rendering',
            },
            {
                label: 'Mobile UI Systems',
                detail: 'Cross-platform React Native iOS and Android architectures with Redux state',
            },
            {
                label: 'Performance & CWV',
                detail: 'Sub-1s First Contentful Paint (FCP) and zero layout shifts (CLS)',
            },
            {
                label: 'Design Systems',
                detail: 'Modular component-driven architectures with strict WCAG AAA accessibility',
            },
            {
                label: 'State Orchestration',
                detail: 'Predictable client store, custom React hooks & strict type contracts',
            },
        ],
    },
    {
        id: 'cloud',
        title: 'Cloud Infrastructure & DevOps',
        badge: 'Reliability & Deployment',
        description: 'Automating immutable container lifecycles, zero-downtime deployment pipelines, and defensive reverse-proxy network topologies.',
        technologies: [
            'AWS (EC2/S3/CloudFront)',
            'AWS Lambda',
            'Docker Containers',
            'GitHub Actions CI/CD',
            'Nginx Reverse Proxy',
            'PM2 Clusters',
            'Linux & Shell Scripting',
            'SSL / TLS & HSTS',
        ],
        competencies: [
            {
                label: 'Containerization',
                detail: 'Multi-stage Docker builds optimized for minimal attack surfaces and footprints',
            },
            {
                label: 'Cloud Infrastructure',
                detail: 'AWS EC2 compute provisioning, S3 asset buckets & CloudFront CDN edges',
            },
            {
                label: 'CI/CD Automation',
                detail: 'Automated GitHub Actions deployment workflows with strict lint and test gates',
            },
            {
                label: 'Gateway & High-Availability',
                detail: 'Nginx reverse proxy load balancing, SSL/TLS termination & PM2 clustering',
            },
        ],
    },
    {
        id: 'data',
        title: 'Data Modeling & AI Platform Rails',
        badge: 'Persistence & Intelligent Data',
        description: 'Architecting polyglot storage topologies balancing relational consistency, document indexing, in-memory caching, and vector data retrieval.',
        technologies: [
            'MongoDB Atlas',
            'MySQL',
            'PostgreSQL (pgvector)',
            'DynamoDB',
            'Redis Distributed Cache',
            'Google BigQuery',
            'Streaming Inference (SSE)',
            'LLM API Gateways',
            'Mongoose & ORMs',
        ],
        competencies: [
            {
                label: 'NoSQL Document Store',
                detail: 'MongoDB Atlas modeling with compound indexing and aggregation pipelines',
            },
            {
                label: 'Relational Schemas',
                detail: 'Structured MySQL & PostgreSQL table architecture, normalization & strict ACID transactions',
            },
            {
                label: 'Key-Value & In-Memory',
                detail: 'DynamoDB scalable collections + distributed Redis caching with TTL policies',
            },
            {
                label: 'AI & Analytical Rails',
                detail: 'BigQuery telemetry warehousing and vector-ready PostgreSQL caching for AI workflows',
            },
        ],
    },
];

export default skillDomains;
